import { useEffect, useRef, useState } from 'react';
import {
  AlertCircleIcon,
  CheckCircle2Icon,
  DownloadIcon,
  Loader2Icon,
  RotateCcwIcon,
  XCircleIcon,
} from 'lucide-react';
import {
  documentGenerationToolInputSchema,
  documentGenerationToolOutputSchema,
} from '@shared/schemas/document-generation';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useChatExperience } from '../chat-experience';
import { buildCodegenChatDocument, buildGenerationCanvasDocument } from '../codegen-document';
import { getChatErrorMessage } from '../chat-errors';
import { hasGenerationPlan } from '../document-canvas/generation-progress';
import { downloadRemoteFile } from '../download-remote-file';
import {
  formatDocumentFormatLabel,
  formatFileSize,
  formatUpdatedAt,
} from '../generated-document-format';
import { isTerminalGenerationStatus, useDocumentGenerationStatus } from '../hooks/useDocumentGeneration';
import { DocumentGenerationCardMenu } from './DocumentGenerationCardMenu';
import {
  CARD_SHELL_CLASS,
  CARD_SHELL_INTERACTIVE_CLASS,
  CardTile,
  MetaSeparator,
  PILL_BUTTON_CLASS,
  ROUND_BUTTON_CLASS,
  STRETCHED_HIT_AREA_CLASS,
  StatusBadge,
} from './DocumentGenerationCardParts';
import { DocumentGenerationStepper } from './DocumentGenerationStepper';
import './web-research-activity.css';

type ChatDocumentGenerationToolProps = {
  errorText?: string;
  input?: unknown;
  output?: unknown;
  state: 'approval-requested'
    | 'approval-responded'
    | 'input-available'
    | 'input-streaming'
    | 'output-available'
    | 'output-denied'
    | 'output-error';
};

// Une génération n'ouvre le canvas toute seule qu'une fois : si l'utilisateur le ferme,
// on ne le lui rouvre pas à chaque remontage de la carte (liste virtualisée, retour d'onglet).
const autoOpenedGenerationIds = new Set<string>();

export function ChatDocumentGenerationTool({
  errorText,
  input,
  output,
  state,
}: ChatDocumentGenerationToolProps) {
  const { openDocument } = useChatExperience();
  const parsedInput = documentGenerationToolInputSchema.safeParse(input);
  const parsedOutput = documentGenerationToolOutputSchema.safeParse(output);
  const accepted = parsedOutput.success && parsedOutput.data.status === 'pending'
    ? parsedOutput.data
    : null;
  const statusQuery = useDocumentGenerationStatus(accepted?.conversationId, accepted?.generationId);
  const [isOpeningPreview, setIsOpeningPreview] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const sawInProgressRef = useRef(false);

  const generation = statusQuery.data ?? null;
  const title = accepted?.title
    ?? (parsedInput.success ? parsedInput.data.title : 'Document riche');
  const kind = accepted?.kind
    ?? (parsedOutput.success ? parsedOutput.data.kind : parsedInput.success ? parsedInput.data.kind : null);
  const isBlocks = generation?.engine === 'blocks';
  const hasPlan = isBlocks && hasGenerationPlan(generation?.progress);

  const openCanvas = () => {
    if (!accepted) {
      return;
    }
    openDocument(buildGenerationCanvasDocument({ generationId: accepted.generationId, kind, title }));
  };

  // « Voir le document se construire » : le canvas s'ouvre dès que le plan existe, mais seulement
  // pour une génération qu'on a vue en cours (pas pour une carte d'historique déjà terminée).
  const generationId = accepted?.generationId;
  const generationStatus = generation?.status;
  useEffect(() => {
    if (!generationId || !generationStatus) {
      return;
    }
    if (!isTerminalGenerationStatus(generationStatus)) {
      sawInProgressRef.current = true;
    }
    if (!sawInProgressRef.current || !hasPlan || autoOpenedGenerationIds.has(generationId)) {
      return;
    }
    autoOpenedGenerationIds.add(generationId);
    openDocument(buildGenerationCanvasDocument({ generationId, kind, title }));
  }, [generationId, generationStatus, hasPlan, kind, openDocument, title]);

  const resolveFreshResult = async () => {
    const fresh = await statusQuery.refetch();
    if (fresh.data?.status !== 'succeeded') {
      throw fresh.error ?? new Error('download_url_unavailable');
    }
    return fresh.data.result;
  };

  const handlePreview = async () => {
    if (isOpeningPreview || isDownloading) {
      return;
    }
    if (isBlocks) {
      openCanvas();
      return;
    }
    setIsOpeningPreview(true);
    try {
      const result = await resolveFreshResult();
      openDocument(buildCodegenChatDocument({ result, title }));
    } catch (error) {
      toast.error(getChatErrorMessage(error));
    } finally {
      setIsOpeningPreview(false);
    }
  };

  const handleDownload = async () => {
    if (isDownloading || isOpeningPreview) {
      return;
    }
    setIsDownloading(true);
    try {
      const result = await resolveFreshResult();
      await downloadRemoteFile(result.downloadUrl, result.filename);
    } catch (error) {
      toast.error(getChatErrorMessage(error));
    } finally {
      setIsDownloading(false);
    }
  };

  const failureCode = parsedOutput.success && parsedOutput.data.status === 'error'
    ? parsedOutput.data.errorCode
    : generation?.status === 'failed' || generation?.status === 'dead'
      ? generation.errorCode
      : null;
  const isSucceeded = generation?.status === 'succeeded';
  const isGenerating = !failureCode && !isSucceeded && (
    state === 'input-streaming'
    || state === 'input-available'
    || generation?.status === 'pending'
    || generation?.status === 'generating'
  );
  // Le polling est arrêté mais la génération n'est pas finie : jamais de carte figée.
  const isStalled = Boolean(accepted) && !failureCode && !isSucceeded && statusQuery.pollingExhausted;
  const isFailed = Boolean(failureCode);

  if (isSucceeded) {
    const { result } = generation;
    const updatedLabel = formatUpdatedAt(result.completedAt);
    const metaItems = [
      result.revisionNumber ? `Version ${result.revisionNumber}` : null,
      formatDocumentFormatLabel(result.kind ?? kind, result.filename),
      formatFileSize(result.sizeBytes),
    ].filter((item): item is string => Boolean(item));
    const currentVersion = result.generatedDocumentId && result.revisionNumber
      ? {
        generatedDocumentId: result.generatedDocumentId,
        generationId: result.generationId,
        revisionNumber: result.revisionNumber,
      }
      : undefined;
    const iconClass = 'size-[1.0625rem]';

    return (
      <div className={cn(CARD_SHELL_CLASS, CARD_SHELL_INTERACTIVE_CLASS)} data-testid="document-generation-card">
        <div className="flex flex-col gap-4 pl-5 pr-5 pb-4 pt-5 @[42rem]:flex-row @[42rem]:pr-4 @[42rem]:items-start">
          <button
            aria-label={`Consulter ${title}`}
            className={cn('flex min-w-0 flex-1 items-start gap-[1.125rem] text-left', STRETCHED_HIT_AREA_CLASS)}
            onClick={() => void handlePreview()}
            type="button"
          >
            <CardTile kind={kind} />
            <span className="min-w-0 flex-1">
              <span className="mt-px line-clamp-2 block text-[18px] font-bold leading-[1.35] tracking-[-0.02em] text-foreground">
                {title}
              </span>
              <span className="mt-1.5 flex flex-wrap items-center gap-x-[15px] gap-y-1.5 text-xs text-foreground/60">
                <StatusBadge
                  icon={isOpeningPreview
                    ? <Loader2Icon className="size-3.5 animate-spin" aria-hidden />
                    : <CheckCircle2Icon className="size-3.5" aria-hidden />}
                  tone="success"
                >
                  Terminé
                </StatusBadge>
                {metaItems.map((item) => (
                  <span className="inline-flex items-center gap-[15px]" key={item}>
                    <MetaSeparator />
                    <span>{item}</span>
                  </span>
                ))}
              </span>
              {updatedLabel ? (
                <span className="mt-[7px] block text-xs text-muted-foreground">{updatedLabel}</span>
              ) : null}
            </span>
          </button>
          <div className="relative z-10 flex shrink-0 flex-wrap items-center gap-2 @[42rem]:h-[4.5rem]">
            <Button
              aria-label={`Télécharger ${title}`}
              className={PILL_BUTTON_CLASS}
              disabled={isDownloading || isOpeningPreview}
              onClick={() => void handleDownload()}
              type="button"
              variant="outline"
            >
              {isDownloading
                ? <Loader2Icon className={cn(iconClass, 'animate-spin')} aria-hidden />
                : <DownloadIcon className={iconClass} aria-hidden />}
              Télécharger
            </Button>
            {accepted?.conversationId ? (
              <DocumentGenerationCardMenu
                conversationId={accepted.conversationId}
                currentVersion={currentVersion}
                disabled={isDownloading || isOpeningPreview}
                isBlocks={isBlocks}
                kind={kind}
                onOpenPreview={() => void handlePreview()}
                title={title}
                triggerClassName={ROUND_BUTTON_CLASS}
              />
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  const showSteps = isBlocks && isGenerating && !isStalled;
  if (showSteps) {
    // En cours : pas d'action (le fichier n'existe pas encore). Toute la carte ouvre l'aperçu en
    // construction dès que le plan existe, via un bouton frère étiré — pas de bouton imbriqué.
    return (
      <div aria-busy className={CARD_SHELL_CLASS} data-testid="document-generation-card">
        <div className="flex items-center gap-4 px-5 py-[1.3125rem]">
          <CardTile compact kind={kind} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[18px] font-medium leading-[1.2] tracking-[-0.02em] text-foreground">{title}</p>
            <DocumentGenerationStepper
              className="mt-2"
              generationId={accepted?.generationId}
              progress={generation?.progress}
              status={generation?.status}
            />
          </div>
        </div>
        {hasPlan ? (
          <button
            aria-label={`Voir ${title} se construire`}
            className="absolute inset-0 cursor-pointer rounded-[1.5rem] outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
            onClick={openCanvas}
            type="button"
          />
        ) : null}
      </div>
    );
  }

  const statusText = isFailed
    ? getChatErrorMessage({ code: failureCode })
    : isStalled
      ? 'La génération prend plus de temps que prévu.'
      : isGenerating
        ? 'Génération en cours'
        : errorText
          ? getChatErrorMessage(new Error(errorText))
          : 'Préparation de la génération';
  const pending = isGenerating && !isStalled;

  return (
    <div
      aria-busy={pending}
      className={cn(CARD_SHELL_CLASS, isFailed && 'border-destructive/25')}
      data-testid="document-generation-card"
    >
      <div className="flex flex-col gap-4 p-5 @[42rem]:flex-row @[42rem]:items-center">
        <div className="flex min-w-0 flex-1 items-start gap-[1.125rem]">
          <CardTile compact={pending} kind={kind} />
          <div className="min-w-0 flex-1">
            <p className="line-clamp-2 text-[18px] font-semibold leading-[1.35] tracking-[-0.02em] text-foreground">{title}</p>
            <div
              aria-live="polite"
              className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground"
              role="status"
            >
              {isFailed ? (
                <StatusBadge icon={<XCircleIcon className="size-3.5" aria-hidden />} tone="danger">Échec</StatusBadge>
              ) : isStalled ? (
                <StatusBadge icon={<AlertCircleIcon className="size-3.5" aria-hidden />} tone="warning">En attente</StatusBadge>
              ) : (
                <Loader2Icon className="size-3.5 shrink-0 animate-spin" aria-hidden />
              )}
              {pending
                ? <span className="t-shimmer" data-text={statusText}>{statusText}</span>
                : <span>{statusText}</span>}
            </div>
          </div>
        </div>
        {isStalled ? (
          <Button
            className={cn(PILL_BUTTON_CLASS, 'shrink-0 self-start @[42rem]:self-center')}
            onClick={() => void statusQuery.restartPolling()}
            type="button"
            variant="outline"
          >
            <RotateCcwIcon className="size-[1.0625rem]" aria-hidden />
            Réessayer
          </Button>
        ) : null}
      </div>
    </div>
  );
}
